import useSWR from 'hooks/useSWR';
import { useSWRResource } from 'util/resource/useSWRResource';
import { userAPIUrls } from 'apis/CompanyAPI/users/usersApi';
import { BoardroomUpdateDTO } from 'pages/Boardroom/Updates/types';
import { RoomUpdateDTO } from 'pages/Rooms/Updates/types';
import { PaginatedResult } from 'types';
import { PaginationOptions } from 'types/api';
import { CommunityUpdate, CompanyUpdate } from 'types/content';
import { useMemo } from 'react';

// pinned: the backend put this item at the top of the "All" feed, ahead of newer items
export type UserFeedData = (
  | {
      type: 'boardroom';
      boardroomUpdate: BoardroomUpdateDTO;
    }
  | {
      type: 'room';
      roomUpdate: RoomUpdateDTO;
    }
  | {
      type: 'company';
      companyUpdate: CompanyUpdate;
    }
  | {
      type: 'community';
      communityUpdate: CommunityUpdate;
    }
) & { pinned?: boolean };

export type UserFeedAudience = 'following' | 'shortlisted' | 'all';

export type UserFeedSortBy = 'date' | 'relevance';

export interface UserFeedFilters {
  endDate?: Date;
  audience?: UserFeedAudience;
  sortBy?: UserFeedSortBy;
}

export const useUserFeedSWR = (
  startDate: Date,
  additionalFilters?: UserFeedFilters,
  paginationOptions?: PaginationOptions,
) => {
  // Create a stable cache key that includes all the parameters
  const cacheKey = useMemo(() => {
    return userAPIUrls.feed({
      startTime: startDate.toISOString(),
      endTime: additionalFilters?.endDate?.toISOString(),
      audience: additionalFilters?.audience,
      sortBy: additionalFilters?.sortBy,
      limit: paginationOptions?.limit || 50,
      page: paginationOptions?.page || 1,
    });
  }, [startDate, additionalFilters, paginationOptions]);

  const result = useSWR<PaginatedResult<UserFeedData>>(
    cacheKey,
    undefined, // fallbackData
    {
      // SWR caching options
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000, // 30 seconds deduping
      focusThrottleInterval: 60000, // 1 minute throttle
      keepPreviousData: true,
    },
  );

  return useSWRResource<PaginatedResult<UserFeedData>>(result);
};
